package ui.components

import androidx.compose.runtime.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.*
import domain.model.*
import kotlinx.browser.window

/**
 * Settings modal showing organizations, invitations, and logout option
 * Temporary UI to give users visibility into their GitHub App installations
 */
@Composable
fun SettingsModal(
    isOpen: Boolean,
    onClose: () -> Unit,
    organizations: List<OrganizationInsights>,
    pendingInvitations: List<OrganizationInvitation>,
    onLogout: () -> Unit,
    onRefreshOrganizations: () -> Unit = {}
) {
    if (isOpen) {
        // Modal overlay
        Div({
            style {
                position(Position.Fixed)
                top(0.px)
                left(0.px)
                width(100.percent)
                height(100.percent)
                backgroundColor(Color("rgba(0, 0, 0, 0.5)"))
                property("z-index", "1000")
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
            }
            onClick { onClose() }
        }) {
            // Modal content
            Div({
                style {
                    backgroundColor(Color.white)
                    borderRadius(8.px)
                    padding(24.px)
                    maxWidth(600.px)
                    maxHeight(80.percent)
                    width(90.percent)
                    property("box-shadow", "0 10px 25px rgba(0, 0, 0, 0.2)")
                    overflowY("auto")
                }
                onClick { event -> event.stopPropagation() }
            }) {
                // Header
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                        marginBottom(20.px)
                        paddingBottom(12.px)
                        property("border-bottom", "1px solid #e5e7eb")
                    }
                }) {
                    H3({
                        style {
                            margin(0.px)
                            fontSize(20.px)
                            fontWeight("600")
                            color(Color("#1f2937"))
                        }
                    }) {
                        Text("⚙️ Settings")
                    }
                    
                    Button({
                        style {
                            backgroundColor(Color.transparent)
                            border(0.px)
                            fontSize(24.px)
                            cursor("pointer")
                            padding(4.px)
                            borderRadius(4.px)
                        }
                        onClick { onClose() }
                    }) {
                        Text("×")
                    }
                }
                
                // Organizations section
                Div({
                    style {
                        marginBottom(24.px)
                    }
                }) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            justifyContent(JustifyContent.SpaceBetween)
                            alignItems(AlignItems.Center)
                            marginBottom(12.px)
                        }
                    }) {
                        H4({
                            style {
                                margin(0.px)
                                fontSize(16.px)
                                fontWeight("600")
                                color(Color("#374151"))
                            }
                        }) {
                            Text("🏢 Organizations (${organizations.size})")
                        }
                        
                        Button({
                            style {
                                backgroundColor(Color("#f3f4f6"))
                                border(1.px, LineStyle.Solid, Color("#d1d5db"))
                                borderRadius(4.px)
                                padding(4.px, 8.px)
                                fontSize(12.px)
                                cursor("pointer")
                            }
                            onClick { onRefreshOrganizations() }
                        }) {
                            Text("🔄 Refresh")
                        }
                    }
                    
                    if (organizations.isEmpty()) {
                        Div({
                            style {
                                padding(16.px)
                                backgroundColor(Color("#fef3c7"))
                                borderRadius(6.px)
                                fontSize(14.px)
                                color(Color("#92400e"))
                            }
                        }) {
                            Text("⚠️ No GitHub App installations found. Install the app in your organizations to see PRs.")
                        }
                    } else {
                        organizations.forEach { orgInsight ->
                            OrganizationCard(orgInsight)
                        }
                    }
                }
                
                // Invitations section
                if (pendingInvitations.isNotEmpty()) {
                    Div({
                        style {
                            marginBottom(24.px)
                        }
                    }) {
                        H4({
                            style {
                                margin(0.px, 0.px, 12.px, 0.px)
                                fontSize(16.px)
                                fontWeight("600")
                                color(Color("#374151"))
                            }
                        }) {
                            Text("✉️ Pending Invitations (${pendingInvitations.size})")
                        }
                        
                        pendingInvitations.forEach { invitation ->
                            InvitationCard(invitation)
                        }
                    }
                }
                
                // Actions section
                Div({
                    style {
                        paddingTop(16.px)
                        property("border-top", "1px solid #e5e7eb")
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                    }
                }) {
                    Button({
                        style {
                            backgroundColor(Color("#dc2626"))
                            color(Color.white)
                            border(0.px)
                            borderRadius(6.px)
                            padding(8.px, 16.px)
                            fontSize(14.px)
                            fontWeight("500")
                            cursor("pointer")
                        }
                        onClick { 
                            onLogout()
                            onClose()
                        }
                    }) {
                        Text("🚪 Logout")
                    }
                    
                    Button({
                        style {
                            backgroundColor(Color("#6b7280"))
                            color(Color.white)
                            border(0.px)
                            borderRadius(6.px)
                            padding(8.px, 16.px)
                            fontSize(14.px)
                            fontWeight("500")
                            cursor("pointer")
                        }
                        onClick { onClose() }
                    }) {
                        Text("Close")
                    }
                }
            }
        }
    }
}

@Composable
private fun OrganizationCard(orgInsight: OrganizationInsights) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            padding(12.px)
            border(1.px, LineStyle.Solid, Color("#e5e7eb"))
            borderRadius(6.px)
            marginBottom(8.px)
        }
    }) {
        Img(src = orgInsight.organization.avatarUrl) {
            style {
                width(32.px)
                height(32.px)
                borderRadius(16.px)
                marginRight(12.px)
            }
        }
        
        Div({
            style {
                flexGrow(1)
            }
        }) {
            Div({
                style {
                    fontWeight("600")
                    fontSize(14.px)
                    color(Color("#1f2937"))
                }
            }) {
                Text(orgInsight.organization.name ?: orgInsight.organization.login)
            }
            
            Div({
                style {
                    fontSize(12.px)
                    color(Color("#6b7280"))
                }
            }) {
                Text("@${orgInsight.organization.login}")
            }
        }
        
        Div({
            style {
                fontSize(11.px)
                padding(4.px, 8.px)
                backgroundColor(Color("#10b981"))
                color(Color.white)
                borderRadius(12.px)
                fontWeight("500")
            }
        }) {
            Text("App Installed")
        }
    }
}

@Composable
private fun InvitationCard(invitation: OrganizationInvitation) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.SpaceBetween)
            padding(12.px)
            border(1.px, LineStyle.Solid, Color("#fbbf24"))
            borderRadius(6.px)
            backgroundColor(Color("#fffbeb"))
            marginBottom(8.px)
        }
    }) {
        Div({
            style {
                flexGrow(1)
            }
        }) {
            Div({
                style {
                    fontWeight("600")
                    fontSize(14.px)
                    color(Color("#92400e"))
                }
            }) {
                Text("Organization invitation")
            }
            
            Div({
                style {
                    fontSize(12.px)
                    color(Color("#a16207"))
                }
            }) {
                Text("Role: ${invitation.role}")
            }
        }
        
        Button({
            style {
                backgroundColor(Color("#f59e0b"))
                color(Color.white)
                border(0.px)
                borderRadius(4.px)
                padding(6.px, 12.px)
                fontSize(12.px)
                fontWeight("500")
                cursor("pointer")
            }
            onClick { 
                window.open(invitation.invitationUrl, "_blank")
            }
        }) {
            Text("View")
        }
    }
}